<template>
  <div>
    <div class="declaration-header-container">
      <h3 class="declaration-title">BẢNG KÊ KHAI HÀNG HÓA</h3>
      <div class="declaration-header-actions">
        <v-btn
          class="btn-save mr-2"
          :disabled="!hasChanges || isSaving"
          :loading="isSaving"
          @click="saveAllDeclarations"
        >
          <v-icon left small>fa-save</v-icon> Lưu lại
        </v-btn>
        <v-btn
          class="btn-add-row mr-2"
          @click="addBlankDeclaration"
        >
          + Thêm dòng mới
        </v-btn>
        <v-btn
          class="btn-paste-row"
          :disabled="!hasCopiedDeclaration"
          @click="pasteAsNewDeclaration"
        >
          <v-icon left small>fa-paste</v-icon> Dán dòng mới
        </v-btn>
      </div>
    </div>

    <div class="table-responsive">
      <table class="custom-declaration-table">
        <thead>
          <tr>
            <th class="col-code">Mã khai báo</th>
            <th class="col-desc">Tên SP, hàng hóa</th>
            <th class="col-unit">Đơn vị tính</th>
            <th class="col-price">Đơn giá dự kiến (₫) </th>
            <th class="col-qty">Số lượng</th>
            <th class="col-tax">Thuế XNK (%)</th>
            <th class="col-vat">Thuế VAT (%)</th>
            <th class="col-hs">Mã HS</th>
            <th class="col-supplier">NSX</th>
            <th class="col-status">Trạng thái</th>
            <th class="col-action"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in order.Declarations" :key="item.ID">
            <td class="col-code text-center">
              <a
                @click="openDeclarationDetail(item.ID)"
                class="declaration-link font-weight-bold"
              >
                {{ item.MaKB }}
              </a>
              <template v-if="uploadingR2Id === item.ID">
                <div class="upload-dashed-card">
                  <v-progress-circular indeterminate size="16" width="2" color="primary"></v-progress-circular>
                  <span style="margin-top: 4px;">Tải lên...</span>
                </div>
              </template>
              <template v-else-if="!item.HinhAnh">
                <div class="upload-dashed-card" @click="triggerUpload(item)">
                  <v-icon class="upload-icon" small>fa-image</v-icon>
                  <span>Tải lên</span>
                </div>
              </template>
              <template v-else>
                <div class="image-preview-container" @click="openImagePreview(item.HinhAnh)">
                  <img :src="item.HinhAnh" alt="Preview" />
                  <button
                    v-if="isEditable(item)"
                    type="button"
                    class="image-action-btn image-action-btn--delete"
                    @click.stop="removeImage(item)"
                  >
                    <v-icon x-small color="white">fa-times</v-icon>
                  </button>
                </div>
              </template>
            </td>
            <td class="col-desc">
              <div class="desc-cell-wrapper">
                <span class="desc-index">{{ index + 1 }}.</span>
                <div class="desc-content">
                  <textarea
                    v-model="item.MoTa"
                    class="cell-textarea"
                    :class="{ 'cell-textarea--error': (item.MoTa || '').length > 199 }"
                    :disabled="!isEditable(item)"
                    @input="checkChanges"
                    @keyup.enter="$event.target.blur()"
                    rows="4"
                    maxlength="199"
                  ></textarea>
                  <div
                    class="char-count"
                    :class="{ 'char-count--error': (item.MoTa || '').length > 199 }"
                  >
                    {{ (item.MoTa || '').length }}/199
                  </div>
                </div>
              </div>
            </td>
            <td class="col-unit text-center">
              <input
                list="declare-unit-options"
                v-model="item.DonViTinh"
                class="cell-input unit-select text-center"
                :disabled="!isEditable(item)"
                @input="checkChanges"
                @change="checkChanges"
                @keyup.enter="$event.target.blur()"
              />
            </td>
            <td class="col-price text-right">
              <div class="price-input-wrapper">
                <h-currency-input
                  v-model="item.GiaKhaiHQToiThieu"
                  :decimal="0"
                  class="flat-numeric-input text-right"
                  :disabled="!isEditable(item)"
                  @input="checkChanges"
                ></h-currency-input>
              </div>
            </td>
            <td class="col-qty text-center">
              <h-currency-input
                v-model="item.SoLuong"
                :decimal="0"
                class="flat-numeric-input text-center"
                :disabled="!isEditable(item)"
                @input="checkChanges"
              ></h-currency-input>
            </td>
            <td class="col-tax text-center">
              <h-currency-input
                v-model="item.ThueNK"
                :decimal="2"
                class="flat-numeric-input text-center"
                :disabled="!isEditable(item)"
                @input="checkChanges"
              ></h-currency-input>
            </td>
            <td class="col-vat text-center">
              <h-currency-input
                v-model="item.ThueVAT"
                :decimal="2"
                class="flat-numeric-input text-center"
                :disabled="!isEditable(item)"
                @input="checkChanges"
              ></h-currency-input>
            </td>
            <td class="col-hs text-center">
              <div class="hs-cell-wrapper">
                <input
                  type="text"
                  v-model="item.MaHS"
                  placeholder="Nhập mã HS"
                  class="cell-input text-center"
                  :disabled="!isEditable(item)"
                  @input="checkChanges"
                  @keyup.enter="$event.target.blur()"
                />
                <select
                  v-model="item.Consignee"
                  class="importer-select"
                  title="Nhà nhập khẩu"
                  :disabled="!isEditable(item)"
                  @change="checkChanges"
                >
                  <option value="">-- Nhà NK --</option>
                  <option v-for="imp in getImporterOptions(item.Consignee)" :key="imp" :value="imp">
                    {{ imp }}
                  </option>
                </select>
              </div>
            </td>
            <td class="col-supplier">
              <textarea
                v-model="item.TenNhaCungCap"
                placeholder="Nhập NSX"
                class="cell-textarea cell-textarea--supplier"
                :disabled="!isEditable(item)"
                @input="checkChanges"
                @keyup.enter="$event.target.blur()"
                rows="4"
              ></textarea>
            </td>
            <td class="col-status text-center">
              <div class="status-cell-wrapper">
                <select
                  v-model.number="item.Status"
                  class="status-select"
                  :disabled="!isEditable(item)"
                  @change="checkChanges"
                >
                  <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
                    {{ opt.name }}
                  </option>
                </select>
                <textarea
                  v-model="item.ChinhSachNhapKhau"
                  placeholder="Ghi chú..."
                  class="cell-textarea cell-textarea--note"
                  :disabled="!isEditable(item)"
                  @input="checkChanges"
                  rows="2"
                ></textarea>
              </div>
            </td>
            <td class="col-action text-center">
              <div class="action-btn-group">
                <v-btn
                  icon
                  color="primary"
                  small
                  title="Sao chép dòng này"
                  @click="copyDeclaration(item)"
                >
                  <v-icon small>fa-copy</v-icon>
                </v-btn>
                <v-btn
                  icon
                  color="teal"
                  small
                  :disabled="!isEditable(item) || !hasCopiedDeclaration"
                  :title="hasCopiedDeclaration ? 'Dán dữ liệu vào dòng này' : 'Chưa có dữ liệu sao chép'"
                  @click="pasteDeclaration(item)"
                >
                  <v-icon small>fa-paste</v-icon>
                </v-btn>
                <v-btn
                  icon
                  color="error"
                  small
                  :disabled="!isEditable(item)"
                  title="Xóa dòng này"
                  @click="deleteDeclaration(item)"
                >
                  <v-icon small>fa-trash-alt</v-icon>
                </v-btn>
              </div>
            </td>
          </tr>
          <!-- Summary Row -->
          <tr v-if="order.Declarations && order.Declarations.length > 0" class="summary-row">
            <td colspan="3" class="total-label">Tổng cộng</td>
            <td class="total-value total-value--right" title="Tổng thành tiền trước VAT">
              <div class="summary-sublabel">Tiền trước VAT</div>
              <div class="summary-main-val">{{ formatVND(totalAmountBeforeVAT) }}</div>
            </td>
            <td class="total-value total-value--center" title="Tổng số lượng">
              <div class="summary-sublabel">Tổng SL</div>
              <div class="summary-main-val">{{ formatQuantity(totalQuantity) }}</div>
            </td>
            <td class="total-value total-value--right" title="Tổng tiền thuế XNK">
              <div class="summary-sublabel">Tiền thuế XNK</div>
              <div class="summary-main-val">{{ formatVND(totalTaxNKAmount) }}</div>
            </td>
            <td class="total-value total-value--right" title="Tổng tiền thuế VAT">
              <div class="summary-sublabel">Tiền thuế VAT</div>
              <div class="summary-main-val">{{ formatVND(totalTaxVATAmount) }}</div>
            </td>
            <td class="total-value total-value--right" title="Tổng tiền gồm VAT">
              <div class="summary-sublabel">Tổng gồm VAT</div>
              <div class="summary-main-val">{{ formatVND(totalAmountWithVAT) }}</div>
            </td>
            <td colspan="3"></td>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="info-note">
      * Nhấp chuột trực tiếp vào các ô để chỉnh sửa. Dòng tổng cộng sẽ tự động cập nhật khi thay đổi Đơn giá, Số lượng hoặc Thuế suất. Nhấn "Lưu lại" ở góc trên để lưu các thay đổi.
    </p>

    <!-- Hidden file input for inline uploader -->
    <input
      type="file"
      ref="inlineR2Uploader"
      style="display: none"
      @change="uploadInlineR2Image"
      accept="image/*"
    />

    <v-dialog v-model="imagePreviewVisible" max-width="900px">
      <div class="image-preview-dialog">
        <button type="button" class="image-preview-close" @click="imagePreviewVisible = false">
          <v-icon small color="white">fa-times</v-icon>
        </button>
        <img v-if="previewImageUrl" :src="previewImageUrl" alt="Declaration full preview" class="image-preview-dialog__img" />
      </div>
    </v-dialog>
    <datalist id="declare-unit-options">
      <option v-for="unit in declareUnits" :key="unit" :value="unit"></option>
    </datalist>
  </div>
</template>

<script>
import configs from "@/configs";
import { sendSuccessNotice, sendErrorNotice, refreshResource } from "@/commons/utils";

export default {
  name: "TabDeclarations",
  props: {
    order: {
      type: Object,
      required: true
    },
    orderEditable: {
      type: Object,
      required: true
    },
    adminRole: {
      type: Boolean,
      required: true
    },
    internalRole: {
      type: Boolean,
      required: true
    },
    orderRole: {
      type: Number,
      required: true
    },
    declarationHeader: {
      type: Array,
      required: true
    },
    declaration_status_list: {
      type: Array,
      required: true
    },
    orderReadonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploadingR2Id: null,
      uploadingItem: null,
      imagePreviewVisible: false,
      previewImageUrl: "",
      declareUnits: configs.DECLARE_UNITS_LIST,
      statusOptions: configs.DECLARE_STATUS_LIST,
      originalSnapshot: {},
      hasChanges: false,
      isSaving: false,
      hasCopiedDeclaration: false
    };
  },
  mounted() {
    this.checkCopiedDeclaration();
    window.addEventListener("storage", this.onStorageChange);
    window.addEventListener("focus", this.checkCopiedDeclaration);
    if (!this.configuration || !this.configuration.ID) {
      this.$store.dispatch("configuration/getDetail", 1);
    }
  },
  beforeDestroy() {
    window.removeEventListener("storage", this.onStorageChange);
    window.removeEventListener("focus", this.checkCopiedDeclaration);
  },
  watch: {
    "order.ID": {
      immediate: true,
      handler(newId, oldId) {
        if (newId !== oldId) {
          this.takeSnapshot(this.order && this.order.Declarations);
        }
      }
    },
    "order.Declarations": {
      deep: true,
      handler(newVal) {
        this.syncSnapshotWithDeclarations(newVal);
        this.checkChanges();
      }
    }
  },
  computed: {
    totalAmountBeforeVAT() {
      if (!this.order.Declarations) return 0;
      return this.order.Declarations.reduce((sum, item) => {
        const qty = parseFloat(item.SoLuong) || 0;
        const price = parseFloat(item.GiaKhaiHQToiThieu) || 0;
        return sum + (price * qty);
      }, 0);
    },
    totalQuantity() {
      if (!this.order.Declarations) return 0;
      return this.order.Declarations.reduce((sum, item) => {
        const qty = parseFloat(item.SoLuong) || 0;
        return sum + qty;
      }, 0);
    },
    totalTaxNKAmount() {
      if (!this.order.Declarations) return 0;
      return this.order.Declarations.reduce((sum, item) => {
        const qty = parseFloat(item.SoLuong) || 0;
        const price = parseFloat(item.GiaKhaiHQToiThieu) || 0;
        const taxNK = parseFloat(item.ThueNK) || 0;
        return sum + (price * qty * taxNK / 100);
      }, 0);
    },
    totalTaxVATAmount() {
      if (!this.order.Declarations) return 0;
      return this.order.Declarations.reduce((sum, item) => {
        const qty = parseFloat(item.SoLuong) || 0;
        const price = parseFloat(item.GiaKhaiHQToiThieu) || 0;
        const taxVAT = parseFloat(item.ThueVAT) || 0;
        const lineAmount = price * qty;
        const lineTaxVAT = (lineAmount) * (taxVAT / 100);
        return sum + lineTaxVAT;
      }, 0);
    },
    totalAmountWithVAT() {
      return this.totalAmountBeforeVAT + this.totalTaxNKAmount + this.totalTaxVATAmount;
    },
    configuration() {
      return (this.$store.state.configuration && this.$store.state.configuration.selected)
        ? this.$store.state.configuration.selected
        : {};
    },
    importerOptions() {
      const configImporters = this.configuration && this.configuration.Importers ? this.configuration.Importers : "";
      if (!configImporters) return [];
      return configImporters
        .split(",")
        .map(s => s.trim())
        .filter(s => s.length > 0);
    }
  },
  methods: {
    formatVND(val) {
      if (val === null || val === undefined || val === "") return "0 đ";
      const num = parseFloat(val);
      if (isNaN(num)) return "0 đ";
      return Math.round(num).toLocaleString("vi-VN") + " đ";
    },
    formatQuantity(val) {
      if (val === null || val === undefined || val === "") return "0";
      const num = parseFloat(val);
      if (isNaN(num)) return "0";
      return num.toLocaleString("vi-VN");
    },
    getImporterOptions(currentVal) {
      const list = [...this.importerOptions];
      if (currentVal && !list.includes(currentVal)) {
        list.unshift(currentVal);
      }
      return list;
    },
    takeSnapshot(declarations) {
      const snapshot = {};
      (declarations || []).forEach(item => {
        snapshot[item.ID] = this.cloneItem(item);
      });
      this.originalSnapshot = snapshot;
      this.hasChanges = false;
    },
    syncSnapshotWithDeclarations(declarations) {
      if (!declarations) return;
      if (!this.originalSnapshot || Object.keys(this.originalSnapshot).length === 0) {
        this.takeSnapshot(declarations);
        return;
      }
      const currentIds = new Set(declarations.map(d => d.ID));
      declarations.forEach(item => {
        if (!this.originalSnapshot[item.ID]) {
          this.originalSnapshot[item.ID] = this.cloneItem(item);
        }
      });
      Object.keys(this.originalSnapshot).forEach(id => {
        if (!currentIds.has(Number(id))) {
          delete this.originalSnapshot[id];
        }
      });
    },
    cloneItem(item) {
      return {
        MoTa: item.MoTa || "",
        DonViTinh: item.DonViTinh || "",
        GiaKhaiHQToiThieu: Number(item.GiaKhaiHQToiThieu || 0),
        SoLuong: Number(item.SoLuong || 0),
        ThueNK: Number(item.ThueNK || 0),
        ThueVAT: Number(item.ThueVAT || 0),
        MaHS: item.MaHS || "",
        Consignee: item.Consignee || "",
        TenNhaCungCap: item.TenNhaCungCap || "",
        Status: Number(item.Status !== undefined && item.Status !== null ? item.Status : 1),
        HinhAnh: item.HinhAnh || "",
        ChinhSachNhapKhau: item.ChinhSachNhapKhau || ""
      };
    },
    isItemChanged(item) {
      if (!this.originalSnapshot || !item || !item.ID) return false;
      const orig = this.originalSnapshot[item.ID];
      if (!orig) return false;

      if ((item.MoTa || "").trim() !== (orig.MoTa || "").trim()) return true;
      if ((item.DonViTinh || "").trim() !== (orig.DonViTinh || "").trim()) return true;
      if (Number(item.GiaKhaiHQToiThieu || 0) !== orig.GiaKhaiHQToiThieu) return true;
      if (Number(item.SoLuong || 0) !== orig.SoLuong) return true;
      if (Number(item.ThueNK || 0) !== orig.ThueNK) return true;
      if (Number(item.ThueVAT || 0) !== orig.ThueVAT) return true;
      if ((item.MaHS || "").trim() !== (orig.MaHS || "").trim()) return true;
      if ((item.Consignee || "").trim() !== (orig.Consignee || "").trim()) return true;
      if ((item.TenNhaCungCap || "").trim() !== (orig.TenNhaCungCap || "").trim()) return true;
      if (Number(item.Status !== undefined && item.Status !== null ? item.Status : 1) !== orig.Status) return true;
      if ((item.HinhAnh || "") !== (orig.HinhAnh || "")) return true;
      if ((item.ChinhSachNhapKhau || "").trim() !== (orig.ChinhSachNhapKhau || "").trim()) return true;

      return false;
    },
    checkChanges() {
      if (!this.order || !this.order.Declarations || !this.originalSnapshot) {
        this.hasChanges = false;
        return;
      }
      this.hasChanges = this.order.Declarations.some(item => this.isItemChanged(item));
    },
    openDeclarationDetail(id) {
      let routeData = this.$router.resolve({
        name: "DeclarationDetail",
        params: { Pid: id, title: `Chi tiết khai báo (#${id})` }
      });
      window.open(routeData.href, "_blank");
    },
    isEditable(item) {
      return true;
    },
    getDeclareUnitOptions(currentUnit) {
      if (!currentUnit || this.declareUnits.includes(currentUnit)) {
        return this.declareUnits;
      }
      return [currentUnit, ...this.declareUnits];
    },
    async addBlankDeclaration() {
      if (this.hasChanges) {
        const proceed = confirm(
          "Bạn có thay đổi chưa lưu trên bảng kê khai. Bạn có muốn lưu lại trước khi thêm dòng mới không?"
        );
        if (proceed) {
          await this.saveAllDeclarations();
        } else {
          return;
        }
      }
      const newItem = {
        OrderID: this.order.ID,
        Status: 1,
        HinhAnh: "",
        MoTa: "",
        DonViTinh: "PCE",
        GiaKhaiHQToiThieu: 0,
        SoLuong: 0,
        ThueNK: 0,
        ThueVAT: 8,
        MaHS: "",
        Consignee: "",
        TenNhaCungCap: "",
        ChinhSachNhapKhau: ""
      };
      this.$store.dispatch("declaration/setDetail", { data: newItem, id: 0 });
    },
    async saveAllDeclarations() {
      if (!this.hasChanges || this.isSaving) return;
      const invalidItem = (this.order.Declarations || []).find(
        item => (item.MoTa || "").length > 199
      );
      if (invalidItem) {
        sendErrorNotice("Tên SP, hàng hóa không được vượt quá 199 ký tự.");
        return;
      }
      const changedItems = (this.order.Declarations || []).filter(item => this.isItemChanged(item));
      if (changedItems.length === 0) {
        this.hasChanges = false;
        return;
      }

      this.isSaving = true;
      const promises = changedItems.map(item => {
        const payload = { ...item };
        const numericFields = ["GiaKhaiHQToiThieu", "SoLuong", "ThueNK", "ThueVAT"];
        numericFields.forEach(f => {
          if (payload[f] === "" || payload[f] === null || payload[f] === undefined) {
            payload[f] = 0;
          } else {
            payload[f] = parseFloat(payload[f]);
          }
        });
        return this.$http.post(
          `${configs.API || ""}/admin/api/declaration/${payload.ID}`,
          payload,
          {
            headers: {
              Authorization: "Bearer " + this.$store.state.authentication.token
            }
          }
        );
      });

      try {
        const responses = await Promise.all(promises);
        const hasError = responses.some(
          res => res && res.data && (res.data.status === "fail" || res.data.status === false)
        );
        if (hasError) {
          sendErrorNotice("Có lỗi xảy ra khi lưu một số dòng khai báo.");
        } else {
          sendSuccessNotice("Cập nhật khai báo thành công.");
        }
        this.takeSnapshot(this.order.Declarations);
        this.hasChanges = false;
        refreshResource();
      } catch (err) {
        sendErrorNotice("Lỗi kết nối khi lưu khai báo.");
      } finally {
        this.isSaving = false;
      }
    },
    checkCopiedDeclaration() {
      try {
        this.hasCopiedDeclaration = !!localStorage.getItem("namtrung_copied_declaration");
      } catch (e) {
        this.hasCopiedDeclaration = false;
      }
    },
    onStorageChange(e) {
      if (e.key === "namtrung_copied_declaration") {
        this.hasCopiedDeclaration = !!e.newValue;
      }
    },
    copyDeclaration(item) {
      if (!item) return;
      const dataToCopy = {
        MoTa: item.MoTa || "",
        DonViTinh: item.DonViTinh || "PCE",
        GiaKhaiHQToiThieu: item.GiaKhaiHQToiThieu !== undefined && item.GiaKhaiHQToiThieu !== null ? item.GiaKhaiHQToiThieu : 0,
        SoLuong: item.SoLuong !== undefined && item.SoLuong !== null ? item.SoLuong : 0,
        ThueNK: item.ThueNK !== undefined && item.ThueNK !== null ? item.ThueNK : 0,
        ThueVAT: item.ThueVAT !== undefined && item.ThueVAT !== null ? item.ThueVAT : 8,
        MaHS: item.MaHS || "",
        Consignee: item.Consignee || "",
        TenNhaCungCap: item.TenNhaCungCap || "",
        HinhAnh: item.HinhAnh || "",
        ChinhSachNhapKhau: item.ChinhSachNhapKhau || "",
        Status: item.Status !== undefined && item.Status !== null ? item.Status : 1
      };
      try {
        localStorage.setItem("namtrung_copied_declaration", JSON.stringify(dataToCopy));
        this.hasCopiedDeclaration = true;
        sendSuccessNotice("Đã sao chép dòng kê khai vào bộ nhớ tạm.");
      } catch (err) {
        sendErrorNotice("Không thể sao chép dòng kê khai.");
      }
    },
    pasteDeclaration(item) {
      if (!this.isEditable(item)) return;
      try {
        const raw = localStorage.getItem("namtrung_copied_declaration");
        if (!raw) {
          this.hasCopiedDeclaration = false;
          sendErrorNotice("Chưa có dòng kê khai nào được sao chép trong bộ nhớ tạm.");
          return;
        }
        const copied = JSON.parse(raw);
        this.$set(item, "MoTa", copied.MoTa || "");
        this.$set(item, "DonViTinh", copied.DonViTinh || "PCE");
        this.$set(item, "GiaKhaiHQToiThieu", copied.GiaKhaiHQToiThieu !== undefined ? copied.GiaKhaiHQToiThieu : 0);
        this.$set(item, "SoLuong", copied.SoLuong !== undefined ? copied.SoLuong : 0);
        this.$set(item, "ThueNK", copied.ThueNK !== undefined ? copied.ThueNK : 0);
        this.$set(item, "ThueVAT", copied.ThueVAT !== undefined ? copied.ThueVAT : 8);
        this.$set(item, "MaHS", copied.MaHS || "");
        this.$set(item, "Consignee", copied.Consignee || "");
        this.$set(item, "TenNhaCungCap", copied.TenNhaCungCap || "");
        this.$set(item, "HinhAnh", copied.HinhAnh || "");
        this.$set(item, "ChinhSachNhapKhau", copied.ChinhSachNhapKhau || "");
        if (copied.Status !== undefined) {
          this.$set(item, "Status", copied.Status);
        }
        localStorage.removeItem("namtrung_copied_declaration");
        this.hasCopiedDeclaration = false;
        this.checkChanges();
        sendSuccessNotice("Đã dán dữ liệu kê khai thành công.");
      } catch (err) {
        sendErrorNotice("Lỗi khi dán dữ liệu kê khai.");
      }
    },
    async pasteAsNewDeclaration() {
      const raw = localStorage.getItem("namtrung_copied_declaration");
      if (!raw) {
        this.hasCopiedDeclaration = false;
        sendErrorNotice("Chưa có dòng kê khai nào được sao chép trong bộ nhớ tạm.");
        return;
      }
      let copied = {};
      try {
        copied = JSON.parse(raw);
      } catch (e) {
        sendErrorNotice("Dữ liệu sao chép không hợp lệ.");
        return;
      }

      if (this.hasChanges) {
        const proceed = confirm(
          "Bạn có thay đổi chưa lưu trên bảng kê khai. Bạn có muốn lưu lại trước khi dán dòng mới không?"
        );
        if (proceed) {
          await this.saveAllDeclarations();
        } else {
          return;
        }
      }

      const newItem = {
        OrderID: this.order.ID,
        Status: copied.Status !== undefined ? copied.Status : 1,
        HinhAnh: copied.HinhAnh || "",
        MoTa: copied.MoTa || "",
        DonViTinh: copied.DonViTinh || "PCE",
        GiaKhaiHQToiThieu: copied.GiaKhaiHQToiThieu !== undefined ? copied.GiaKhaiHQToiThieu : 0,
        SoLuong: copied.SoLuong !== undefined ? copied.SoLuong : 0,
        ThueNK: copied.ThueNK !== undefined ? copied.ThueNK : 0,
        ThueVAT: copied.ThueVAT !== undefined ? copied.ThueVAT : 8,
        MaHS: copied.MaHS || "",
        Consignee: copied.Consignee || "",
        TenNhaCungCap: copied.TenNhaCungCap || "",
        ChinhSachNhapKhau: copied.ChinhSachNhapKhau || ""
      };
      localStorage.removeItem("namtrung_copied_declaration");
      this.hasCopiedDeclaration = false;
      this.$store.dispatch("declaration/setDetail", { data: newItem, id: 0 });
      sendSuccessNotice("Đã thêm và dán dòng kê khai mới thành công.");
    },
    deleteDeclaration(item) {
      if (confirm("Bạn có chắc chắn muốn xóa khai báo này không?")) {
        if (this.originalSnapshot && this.originalSnapshot[item.ID]) {
          delete this.originalSnapshot[item.ID];
        }
        this.$store.dispatch("declaration/removeDetail", item.ID);
      }
    },
    triggerUpload(item) {
      if (!this.isEditable(item)) return;
      this.uploadingItem = item;
      this.$refs.inlineR2Uploader.click();
    },
    openImagePreview(url) {
      if (!url) return;
      this.previewImageUrl = url;
      this.imagePreviewVisible = true;
    },
    removeImage(item) {
      if (!this.isEditable(item)) return;
      if (!confirm("Bạn có chắc chắn muốn xóa hình ảnh này không?")) return;
      this.$set(item, "HinhAnh", "");
      this.checkChanges();
    },
    uploadInlineR2Image(e) {
      let file = e.target.files[0];
      if (!file) return;

      this.uploadingR2Id = this.uploadingItem.ID;
      let formData = new FormData();
      formData.append("file", file);

      this.$http
        .post(`${configs.API || ""}/admin/api/upload/r2/`, formData, {
          headers: { Authorization: "Bearer " + this.$store.state.authentication.token }
        })
        .then(res => {
          this.uploadingR2Id = null;
          if (res.data.status === 200 && res.data.data && res.data.data.url) {
            this.$set(this.uploadingItem, "HinhAnh", res.data.data.url);
            this.checkChanges();
          } else {
            sendErrorNotice("Upload thất bại: " + (res.data.message || res.data.error || ""));
          }
        })
        .catch(err => {
          this.uploadingR2Id = null;
          sendErrorNotice("Lỗi kết nối khi upload ảnh.");
        });
      this.$refs.inlineR2Uploader.value = "";
    }
  }
};
</script>

<style scoped>
.declaration-header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  background-color: white;
}
.declaration-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e62a1;
  margin: 0;
}
.declaration-header-actions {
  display: flex;
  align-items: center;
}
.btn-save {
  background-color: #1e62a1 !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 6px !important;
  padding: 6px 16px !important;
  height: 36px !important;
  font-size: 14px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.2s ease;
}
.btn-save:not(:disabled):hover {
  background-color: #154c7d !important;
}
.btn-save:disabled,
.btn-save[disabled] {
  background-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}
.btn-add-row {
  background-color: #0fa140 !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 6px !important;
  padding: 6px 16px !important;
  height: 36px !important;
  font-size: 14px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}
.btn-paste-row {
  background-color: #0d9488 !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 6px !important;
  padding: 6px 14px !important;
  height: 36px !important;
  font-size: 14px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}
.btn-paste-row:not(:disabled):hover {
  background-color: #0f766e !important;
}
.btn-paste-row:disabled,
.btn-paste-row[disabled] {
  background-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

.custom-declaration-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  font-size: 13px;
}
.custom-declaration-table th,
.custom-declaration-table td {
  border: 1px solid #cbd5e1 !important;
  padding: 10px 8px;
  vertical-align: middle;
}
.custom-declaration-table th {
  background-color: #1e62a1 !important;
  color: white !important;
  font-weight: 600;
  font-size: 13px;
  text-align: center;
}

/* Col specific widths & alignments */
.col-code {
  width: 90px;
  min-width: 90px;
  text-align: center;
}
.col-image {
  width: 80px;
  min-width: 80px;
  text-align: center;
}
.col-desc {
  text-align: left;
  min-width: 280px;
  padding-bottom: 4px !important;
}
.col-unit {
  width: 85px;
  min-width: 80px;
}
.col-price {
  width: 130px;
  min-width: 120px;
}
.col-qty {
  width: 85px;
  min-width: 75px;
}
.col-tax {
  width: 110px;
  min-width: 100px;
}
.col-vat {
  width: 110px;
  min-width: 100px;
}
.col-hs {
  width: 140px;
  min-width: 125px;
}
.hs-cell-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}
.importer-select {
  width: 100%;
  font-size: 11px;
  padding: 4px 18px 4px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  outline: none;
  background-color: #ffffff;
  color: #334155;
  height: 28px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 6px center;
  background-size: 10px;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.importer-select:focus {
  border-color: #3b82f6;
}
.col-supplier {
  width: 200px;
  min-width: 160px;
}
.col-status {
  width: 140px;
  min-width: 130px;
}
.col-action {
  width: 95px;
  min-width: 95px;
}
.action-btn-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

/* Link */
.declaration-link {
  color: #1e62a1 !important;
  text-decoration: underline;
  cursor: pointer;
}

/* Inputs styling */
.cell-input,
.cell-textarea {
  width: 100%;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 4px 6px;
  font-size: 13px;
  background-color: transparent;
  transition: all 0.15s ease-in-out;
  color: #334155;
  box-sizing: border-box;
}
.cell-input:hover,
.cell-textarea:hover {
  border-color: #cbd5e1;
  background-color: #f8fafc;
}
.cell-input:focus,
.cell-textarea:focus {
  border-color: #3b82f6;
  background-color: #ffffff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}
.cell-input::placeholder {
  color: #94a3b8;
  font-style: italic;
}

.unit-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
  padding-right: 20px;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 6px center;
  background-size: 10px;
}
.unit-select::-webkit-calendar-picker-indicator {
  display: none !important;
}

.cell-textarea {
  resize: vertical;
  min-height: 86px;
  line-height: 1.4;
}
.cell-textarea--supplier {
  text-align: left;
}

.desc-cell-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
}
.desc-index {
  font-weight: 600;
  color: #475569;
  padding-top: 6px;
}
.desc-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-width: 0;
}
.char-count {
  font-size: 11px;
  color: #94a3b8;
  text-align: right;
  margin-top: 3px;
  line-height: 1.2;
}
.char-count--error {
  color: #ef4444 !important;
  font-weight: 600;
}
.cell-textarea--error {
  border-color: #ef4444 !important;
}

/* Price cell helper */
.price-input-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
}
.price-badge {
  position: absolute;
  top: -8px;
  right: 6px;
  font-size: 10px;
  color: #94a3b8;
  pointer-events: none;
}
.price-input-wrapper input {
  padding-right: 28px !important;
  text-align: right;
}

/* Image Upload Zone */
.upload-dashed-card {
  width: 60px;
  height: 60px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #f8fafc;
  transition: background-color 0.2s;
  margin: 0 auto;
}
.upload-dashed-card:hover {
  background-color: #f1f5f9;
}
.upload-dashed-card .upload-icon {
  font-size: 16px;
  color: #94a3b8;
  margin-bottom: 2px;
}
.upload-dashed-card span {
  font-size: 9px;
  color: #64748b;
}

.image-preview-container {
  position: relative;
  width: 60px;
  height: 60px;
  margin: 0 auto;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  overflow: hidden;
  cursor: pointer;
}
.image-preview-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.image-preview-container:hover::after {
  content: "Xem ảnh";
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-action-btn {
  position: absolute;
  z-index: 2;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.2);
}

.image-action-btn--delete {
  top: 3px;
  right: 3px;
  width: 18px;
  height: 18px;
  background-color: rgba(220, 38, 38, 0.95);
}

.image-preview-dialog {
  position: relative;
  padding: 16px;
  background: rgba(15, 23, 42, 0.96);
  border-radius: 12px;
  overflow: hidden;
}

.image-preview-dialog__img {
  display: block;
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  background: #0f172a;
}

.image-preview-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 9999px;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* Status select dropdown styling */
.status-select {
  width: 100%;
  font-size: 12px;
  padding: 4px 20px 4px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  outline: none;
  background-color: #ffffff;
  color: #334155;
  height: 28px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 6px center;
  background-size: 10px;
  cursor: pointer;
}
.status-select:focus {
  border-color: #3b82f6;
}

.status-cell-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}
.cell-textarea--note {
  min-height: 46px;
  resize: vertical;
  font-size: 12px;
  line-height: 1.3;
  padding: 4px 6px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background-color: #ffffff;
  text-align: left;
}
.cell-textarea--note:hover {
  border-color: #94a3b8;
  background-color: #f8fafc;
}
.cell-textarea--note:focus {
  border-color: #3b82f6;
  background-color: #ffffff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

/* Footer / Total */
.summary-row td {
  background-color: #f8fafc;
  font-weight: bold !important;
  font-size: 13px;
}
.total-label {
  text-transform: none;
  color: #1e293b;
  text-align: right !important;
  padding-right: 12px !important;
  font-size: 14px;
}
.total-value {
  color: #1e62a1 !important;
  white-space: nowrap;
}
.total-value--right {
  text-align: right !important;
}
.total-value--center {
  text-align: center !important;
}
.summary-sublabel {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.2;
  margin-bottom: 2px;
}
.summary-main-val {
  font-size: 13px;
  font-weight: 700;
  color: #1e62a1;
  line-height: 1.3;
}

/* Tooltip info under table */
.info-note {
  margin-top: 12px;
  font-size: 12px;
  color: #64748b;
  font-style: italic;
  text-align: left;
}
</style>
